/**
 * Web (browser) Key_Store adapter backed by IndexedDB.
 *
 * Persistence strategy
 * --------------------
 * Per the link-workspace design (Requirement 4.3), keys are stored in
 * IndexedDB inside a non-extractable `CryptoKey` wrapper where the Web
 * Crypto API supports it. Modern browsers support structured-cloning of
 * non-extractable `CryptoKey` objects directly into IndexedDB, so the
 * primary path simply puts the `CryptoKey` (or `CryptoKeyPair`) into the
 * `keys` object store.
 *
 * If structured-cloning fails (older browsers), the adapter falls back to
 * a one-time `subtle.exportKey('jwk', ...)` and stores a JWK record. On
 * `get`, the JWK is re-imported via `subtle.importKey` and returned. The
 * exported JWK never crosses any network boundary; it stays inside the
 * caller's IndexedDB on the same device.
 *
 * The store is keyed by `accountId` — the public, base32-encoded form of
 * the first 16 bytes of `SHA-256(public_key)` — so each operation is a
 * direct primary-key lookup.
 */

import {
  KEY_STORE_DB_NAME,
  KEY_STORE_DB_VERSION,
  KEY_STORE_OBJECT_STORE,
  type KeyStore,
} from "./key-store.js";

/**
 * Options for {@link createWebKeyStore}. The `idbFactory` override exists
 * so tests can substitute an in-memory IndexedDB implementation; in
 * production callers leave it unset and the adapter uses
 * `globalThis.indexedDB`.
 */
export interface WebKeyStoreOptions {
  idbFactory?: IDBFactory;
  dbName?: string;
}

/**
 * On-disk shape for the native (structured-clone) path. Holds the
 * `CryptoKey` objects directly. Used whenever the browser supports
 * structured-cloning of `CryptoKey` into IndexedDB (essentially every
 * mainstream browser since ~2017).
 */
interface NativeKeyRecord {
  accountId: string;
  kind: "native";
  privateKey: CryptoKey;
  publicKey: CryptoKey | null;
}

/**
 * On-disk shape for the JWK-fallback path. Used only when structured
 * cloning of the input `CryptoKey` is unsupported. The key must be
 * `extractable: true` for this path to succeed; non-extractable keys
 * paired with a non-supporting browser will surface a clear error.
 */
interface JwkKeyRecord {
  accountId: string;
  kind: "jwk";
  privateKeyJwk: JsonWebKey;
  privateAlgorithm: KeyAlgorithm;
  privateUsages: ReadonlyArray<KeyUsage>;
  publicKeyJwk: JsonWebKey | null;
  publicAlgorithm: KeyAlgorithm | null;
  publicUsages: ReadonlyArray<KeyUsage> | null;
}

type KeyRecord = NativeKeyRecord | JwkKeyRecord;

/**
 * Create a {@link KeyStore} backed by IndexedDB.
 *
 * The returned instance is safe to share across the application; each
 * operation opens, uses, and closes the connection so no long-lived state
 * is kept on the instance itself.
 */
export function createWebKeyStore(opts: WebKeyStoreOptions = {}): KeyStore {
  const idbFactory = opts.idbFactory ?? getGlobalIndexedDb();
  const dbName = opts.dbName ?? KEY_STORE_DB_NAME;

  const openDb = (): Promise<IDBDatabase> => openKeyStoreDb(idbFactory, dbName);

  return {
    async put(accountId, key) {
      assertAccountId(accountId);
      const { privateKey, publicKey } = splitKeyOrPair(key);
      const record = await buildRecord(accountId, privateKey, publicKey);
      const db = await openDb();
      try {
        await runPut(db, record);
      } catch (err) {
        if (isDataCloneError(err) && record.kind === "native") {
          // Structured clone failed despite our probing; retry as JWK.
          const fallback = await exportJwkRecord(
            accountId,
            privateKey,
            publicKey,
          );
          await runPut(db, fallback);
        } else {
          throw err;
        }
      } finally {
        db.close();
      }
    },

    async get(accountId) {
      assertAccountId(accountId);
      const db = await openDb();
      try {
        const record = await runGet(db, accountId);
        if (record === undefined) return null;
        return materializeRecord(record);
      } finally {
        db.close();
      }
    },

    async delete(accountId) {
      assertAccountId(accountId);
      const db = await openDb();
      try {
        await runDelete(db, accountId);
      } finally {
        db.close();
      }
    },

    async has(accountId) {
      assertAccountId(accountId);
      const db = await openDb();
      try {
        const count = await runCount(db, accountId);
        return count > 0;
      } finally {
        db.close();
      }
    },

    async list() {
      const db = await openDb();
      try {
        const keys = await runListKeys(db);
        return keys;
      } finally {
        db.close();
      }
    },
  };
}

// --- internals -------------------------------------------------------------

function getGlobalIndexedDb(): IDBFactory {
  const globalIdb = (globalThis as { indexedDB?: IDBFactory }).indexedDB;
  if (!globalIdb) {
    throw new Error(
      "createWebKeyStore: IndexedDB is not available in the current runtime. " +
        "Pass `idbFactory` explicitly when running outside a browser.",
    );
  }
  return globalIdb;
}

function assertAccountId(accountId: string): void {
  if (typeof accountId !== "string" || accountId.length === 0) {
    throw new TypeError(
      "KeyStore: accountId must be a non-empty string keyed by Account ID.",
    );
  }
}

function isCryptoKey(value: unknown): value is CryptoKey {
  // Avoid `instanceof` here because some test harnesses run with a
  // CryptoKey shape that isn't the global constructor.
  return (
    typeof value === "object" &&
    value !== null &&
    "type" in value &&
    "algorithm" in value &&
    "extractable" in value &&
    "usages" in value
  );
}

function splitKeyOrPair(key: CryptoKey | CryptoKeyPair): {
  privateKey: CryptoKey;
  publicKey: CryptoKey | null;
} {
  if (isCryptoKey(key)) {
    return { privateKey: key, publicKey: null };
  }
  const pair = key as CryptoKeyPair;
  if (!isCryptoKey(pair.privateKey)) {
    throw new TypeError("KeyStore.put: CryptoKeyPair.privateKey is required.");
  }
  return {
    privateKey: pair.privateKey,
    publicKey: isCryptoKey(pair.publicKey) ? pair.publicKey : null,
  };
}

async function buildRecord(
  accountId: string,
  privateKey: CryptoKey,
  publicKey: CryptoKey | null,
): Promise<KeyRecord> {
  // The native path is the preferred storage form: it lets us keep
  // non-extractable keys non-extractable end-to-end. We optimistically
  // return a native record and only fall back to JWK if the IDB write
  // throws a DataCloneError.
  return {
    accountId,
    kind: "native",
    privateKey,
    publicKey,
  };
}

async function exportJwkRecord(
  accountId: string,
  privateKey: CryptoKey,
  publicKey: CryptoKey | null,
): Promise<JwkKeyRecord> {
  if (!privateKey.extractable) {
    throw new Error(
      "KeyStore.put: cannot fall back to JWK persistence because the " +
        "private key is non-extractable and the runtime does not support " +
        "structured-cloning CryptoKey into IndexedDB.",
    );
  }
  const subtle = getSubtle();
  const privateKeyJwk = (await subtle.exportKey("jwk", privateKey)) as JsonWebKey;
  let publicKeyJwk: JsonWebKey | null = null;
  let publicAlgorithm: KeyAlgorithm | null = null;
  let publicUsages: ReadonlyArray<KeyUsage> | null = null;
  if (publicKey !== null) {
    publicKeyJwk = (await subtle.exportKey("jwk", publicKey)) as JsonWebKey;
    publicAlgorithm = publicKey.algorithm;
    publicUsages = [...publicKey.usages];
  }
  return {
    accountId,
    kind: "jwk",
    privateKeyJwk,
    privateAlgorithm: privateKey.algorithm,
    privateUsages: [...privateKey.usages],
    publicKeyJwk,
    publicAlgorithm,
    publicUsages,
  };
}

async function materializeRecord(
  record: KeyRecord,
): Promise<CryptoKey | CryptoKeyPair> {
  if (record.kind === "native") {
    if (record.publicKey !== null) {
      return { privateKey: record.privateKey, publicKey: record.publicKey };
    }
    return record.privateKey;
  }
  // JWK fallback: re-import each half on read.
  const subtle = getSubtle();
  const privateKey = await subtle.importKey(
    "jwk",
    record.privateKeyJwk,
    record.privateAlgorithm as AlgorithmIdentifier,
    false,
    [...record.privateUsages],
  );
  if (record.publicKeyJwk !== null && record.publicAlgorithm !== null) {
    const publicKey = await subtle.importKey(
      "jwk",
      record.publicKeyJwk,
      record.publicAlgorithm as AlgorithmIdentifier,
      true,
      record.publicUsages !== null ? [...record.publicUsages] : [],
    );
    return { privateKey, publicKey };
  }
  return privateKey;
}

function getSubtle(): SubtleCrypto {
  const cryptoObj = (globalThis as { crypto?: Crypto }).crypto;
  if (!cryptoObj || !cryptoObj.subtle) {
    throw new Error(
      "KeyStore: Web Crypto SubtleCrypto API is not available in the " +
        "current runtime; cannot import or export key material.",
    );
  }
  return cryptoObj.subtle;
}

function isDataCloneError(err: unknown): boolean {
  if (typeof err !== "object" || err === null) return false;
  const name = (err as { name?: unknown }).name;
  return name === "DataCloneError";
}

// --- IndexedDB request helpers --------------------------------------------

function openKeyStoreDb(
  factory: IDBFactory,
  dbName: string,
): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = factory.open(dbName, KEY_STORE_DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(KEY_STORE_OBJECT_STORE)) {
        db.createObjectStore(KEY_STORE_OBJECT_STORE, { keyPath: "accountId" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("indexedDB.open failed"));
    request.onblocked = () =>
      reject(new Error("indexedDB.open blocked by another connection"));
  });
}

function runPut(db: IDBDatabase, record: KeyRecord): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(KEY_STORE_OBJECT_STORE, "readwrite");
    const store = tx.objectStore(KEY_STORE_OBJECT_STORE);
    const putReq = store.put(record);
    putReq.onerror = () => reject(putReq.error ?? new Error("put failed"));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("transaction failed"));
    tx.onabort = () => reject(tx.error ?? new Error("transaction aborted"));
  });
}

function runGet(
  db: IDBDatabase,
  accountId: string,
): Promise<KeyRecord | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(KEY_STORE_OBJECT_STORE, "readonly");
    const store = tx.objectStore(KEY_STORE_OBJECT_STORE);
    const req = store.get(accountId);
    req.onsuccess = () => resolve(req.result as KeyRecord | undefined);
    req.onerror = () => reject(req.error ?? new Error("get failed"));
  });
}

function runDelete(db: IDBDatabase, accountId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(KEY_STORE_OBJECT_STORE, "readwrite");
    const store = tx.objectStore(KEY_STORE_OBJECT_STORE);
    const req = store.delete(accountId);
    req.onerror = () => reject(req.error ?? new Error("delete failed"));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("transaction failed"));
    tx.onabort = () => reject(tx.error ?? new Error("transaction aborted"));
  });
}

function runCount(db: IDBDatabase, accountId: string): Promise<number> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(KEY_STORE_OBJECT_STORE, "readonly");
    const store = tx.objectStore(KEY_STORE_OBJECT_STORE);
    const req = store.count(accountId);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("count failed"));
  });
}

function runListKeys(db: IDBDatabase): Promise<string[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(KEY_STORE_OBJECT_STORE, "readonly");
    const store = tx.objectStore(KEY_STORE_OBJECT_STORE);
    const req = store.getAllKeys();
    req.onsuccess = () => {
      const keys = req.result as IDBValidKey[];
      resolve(keys.map((k) => String(k)));
    };
    req.onerror = () => reject(req.error ?? new Error("getAllKeys failed"));
  });
}
